/** Header + navigation shell for the signed-in account screens. */
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { cn, fullName } from '@/lib/format';

const NAV = [
  { to: '/account', label: 'Profile', end: true },
  { to: '/account/organizations', label: 'Organizations', end: false },
  // Shown to every member of an organization; the page itself says so when the
  // visitor is not one of its administrators. Hiding it instead would need the
  // layout to fetch memberships on every account screen to answer a question
  // the page already has to answer anyway.
  { to: '/account/people', label: 'People', end: false },
  { to: '/account/sessions', label: 'Sessions', end: false },
  { to: '/account/password', label: 'Password', end: false },
];

export function AppLayout() {
  const { user } = useAuth();

  return (
    <div className="min-h-svh">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-4">
          <p className="text-lg font-semibold tracking-tight text-brand-700">MindPeer</p>
          <div className="flex items-center gap-4">
            {user && (
              <div className="hidden text-right sm:block">
                <p className="text-sm font-medium text-slate-900">{fullName(user)}</p>
                <p className="text-xs text-slate-500">{user.email}</p>
              </div>
            )}
            <NavLink
              to="/sign-out"
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            >
              Sign out
            </NavLink>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-8">
        <nav className="mb-8 flex gap-1 overflow-x-auto border-b border-slate-200" aria-label="Account">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  '-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <Outlet />
      </div>
    </div>
  );
}
